import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, of, combineLatest, firstValueFrom } from 'rxjs';
import { switchMap, catchError, tap, map, startWith, take } from 'rxjs/operators';
import { WorkshopWork, WorkStatus, Product, WorkItem, WorkCurrentStatus, BillingStatus, NotifiedStatus, Client, Bike, Employee } from '../core/models';
import { FirestoreDataService } from '../core/firestore-data.service';
import { AuthService } from '../core/auth.service';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { DatePipe } from '@angular/common';

declare var bootstrap: any;

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [DatePipe]
})
export class DashboardComponent implements OnInit, AfterViewInit {
  @ViewChild('addWorkModal') addWorkModal!: ElementRef;
  @ViewChild('customItemModal') customItemModal!: ElementRef;
  public bsModal: any;
  public bsCustomItemModal: any;

  allWorkshopWorks$: Observable<WorkshopWork[]>;
  filteredWorkshopWorks$: Observable<(WorkshopWork & { originalIndex: number })[]>;
  allProducts$: Observable<Product[]>;
  manLaborProducts$: Observable<Product[]>;
  partsProducts$: Observable<Product[]>;
  allClients$: Observable<Client[]>;
  filteredClients$: Observable<Client[]>;
  
  mechanics$: Observable<Employee[]>;
  assistants$: Observable<Employee[]>;
  allActiveEmployees$: Observable<Employee[]>; // <-- NUEVO: Lista combinada
  
  workSearchControl: FormControl;
  laborSearchControl: FormControl;
  partSearchControl: FormControl;
  clientSearchControl: FormControl;
  
  selectedWork: WorkshopWork | null = null;
  selectedWorkId$ = new BehaviorSubject<string | null>(null);
  selectedWorkDetails$: Observable<WorkshopWork | null | undefined>;
  selectedBikeDetails$: Observable<Bike | null | undefined>;
  selectedWorkStatus$: Observable<WorkStatus | null>;
  
  statusChangeForm: FormGroup;
  manLaborProductAddForm: FormGroup;
  partsProductAddForm: FormGroup;
  newWorkshopWorkForm: FormGroup;
  customItemForm: FormGroup;
  customItemType: 'Labor' | 'Part' = 'Part';

  selectedClientForNewWork: Client | null = null;
  bikesForSelectedClient$: Observable<Bike[]> = of([]);
  clientItems: string[] = [];
  clientItemInputControl = new FormControl('');

  isCreatingWork = false; 
  isUpdatingStatus = false;
  isAddingLaborItem = false;
  isAddingPartItem = false;
  isRemovingItem = false;
  isUpdatingBillingStatus = false;
  isUpdatingNotifiedStatus = false;

  statusUpdateError: string | null = null;
  addItemError: string | null = null;
  createWorkError: string | null = null;
  removeItemError: string | null = null;

  availableStatusesForChange: { value: WorkCurrentStatus, display: string }[] = [
    { value: 'Received', display: 'Recibido' },
    { value: 'In Progress', display: 'En Progreso' },
    { value: 'Awaiting Parts', display: 'Esperando Repuestos' },
    { value: 'Ready', display: 'Lista' },
    { value: 'Cancelled', display: 'Cancelado/Desestimado' }
  ];
  
  constructor(
    private firestoreDataService: FirestoreDataService,
    public authService: AuthService,
    private fb: FormBuilder,
    private router: Router,
    private datePipe: DatePipe
  ) {
    this.workSearchControl = this.fb.control('');
    this.laborSearchControl = this.fb.control('');
    this.partSearchControl = this.fb.control('');
    this.clientSearchControl = this.fb.control('');

    this.allWorkshopWorks$ = this.firestoreDataService.openWorks$;
    this.allProducts$ = this.firestoreDataService.products$;
    this.allClients$ = this.firestoreDataService.clients$;

    this.mechanics$ = this.firestoreDataService.getEmployees().pipe(
      map(employees => employees.filter(emp => emp.type === 'Mecánico' && emp.isActive))
    );
    this.assistants$ = this.firestoreDataService.getEmployees().pipe(
      map(employees => employees.filter(emp => emp.type === 'Asistente Taller' && emp.isActive))
    );
    
    // --- NUEVO: Lista combinada de todos los empleados activos para el dropdown único ---
    this.allActiveEmployees$ = combineLatest([this.mechanics$, this.assistants$]).pipe(
      map(([mechs, asis]) => [...mechs, ...asis])
    );

    this.filteredWorkshopWorks$ = combineLatest([
      this.allWorkshopWorks$,
      this.workSearchControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([works, searchTerm]) => {
        const worksWithOriginalIndex = works.map((work, index) => ({ ...work, originalIndex: index }));
        if (!searchTerm) { return worksWithOriginalIndex; }
        const lowerSearchTerm = searchTerm.toLowerCase();
        return worksWithOriginalIndex.filter(item =>
          (item.clientName?.toLowerCase().includes(lowerSearchTerm)) ||
          (item.bikeDescription?.toLowerCase().includes(lowerSearchTerm)) ||
          (item.caseNumber?.toLowerCase().includes(lowerSearchTerm))
        );
      })
    );


      this.filteredClients$ = combineLatest([
      this.allClients$,
      this.clientSearchControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([clients, searchTerm]) => {
        if (!searchTerm || typeof searchTerm !== 'string' || this.selectedClientForNewWork) return [];
        const lowerSearchTerm = searchTerm.toLowerCase();
        
        return clients.filter(client => 
          // Buscamos por nombre (minúsculas) O por teléfono
          client.name.toLowerCase().includes(lowerSearchTerm) ||
          (client.phone && client.phone.includes(lowerSearchTerm))
        ).slice(0, 5);
      })
    );

    const baseManLaborProducts$ = this.allProducts$.pipe(map(products => products.filter(p => p.marca === "BICI A PUNTO")));
    this.manLaborProducts$ = combineLatest([baseManLaborProducts$, this.laborSearchControl.valueChanges.pipe(startWith(''))]).pipe(
      map(([products, searchTerm]) => {
        if (!searchTerm) return products;
        const lowerSearchTerm = searchTerm.toLowerCase();
        return products.filter(p => 
          p.nombre.toLowerCase().includes(lowerSearchTerm) ||
          p.id.toLowerCase().includes(lowerSearchTerm)
        );
      })
    );
    const basePartsProducts$ = this.allProducts$.pipe(map(products => products.filter(p => p.marca !== "BICI A PUNTO")));
    this.partsProducts$ = combineLatest([basePartsProducts$, this.partSearchControl.valueChanges.pipe(startWith(''))]).pipe(
      map(([products, searchTerm]) => {
        if (!searchTerm) return products;
        const lowerSearchTerm = searchTerm.toLowerCase();
        return products.filter(p => 
          p.nombre.toLowerCase().includes(lowerSearchTerm) ||
          p.id.toLowerCase().includes(lowerSearchTerm)
        );
      })
    );

    this.statusChangeForm = this.fb.group({ newStatus: ['', Validators.required], statusNotes: [''] });
    
    // --- ACTUALIZADO: Formulario de artículos con un solo dropdown base y uno opcional para Full ---
    this.manLaborProductAddForm = this.fb.group({ 
      selectedProduct: [null, Validators.required], 
      quantity: [1, [Validators.required, Validators.min(1)]],
      assignee1: [null], // El único para la mayoría, o el primero para Full
      assignee2: [null]  // El segundo, solo visible y usado en Full
    });
    this.partsProductAddForm = this.fb.group({ 
      selectedProduct: [null, Validators.required], 
      quantity: [1, [Validators.required, Validators.min(1)]],
      assignee1: [null]
    });
    this.customItemForm = this.fb.group({
      productName: ['', Validators.required],
      unitPrice: [null, [Validators.required, Validators.min(0)]],
      quantity: [1, [Validators.required, Validators.min(1)]],
      assignee1: [null]
    });

    this.newWorkshopWorkForm = this.fb.group({
      selectedBike: [null, Validators.required],
      serviceType: [null, Validators.required],
      description: ['', Validators.required]
    });

    this.selectedWorkDetails$ = this.selectedWorkId$.pipe(
      switchMap(id => id ? this.firestoreDataService.getWorkshopWorkById(id) : of(null)),
      tap(work => this.selectedWork = work || null)
    );
    this.selectedBikeDetails$ = this.selectedWorkDetails$.pipe(
      switchMap(work => work && work.bikeId ? this.firestoreDataService.getBikeById(work.bikeId) : of(null))
    );
    this.selectedWorkStatus$ = this.selectedWorkId$.pipe(
      switchMap(id => id ? this.firestoreDataService.getWorkStatus(id) : of(null))
    );
  }

  ngOnInit(): void {
    this.firestoreDataService.loadOpenWorkshopWorks();
    this.firestoreDataService.loadProducts();
    this.firestoreDataService.loadClients();
  }

  ngAfterViewInit(): void {
    if (this.addWorkModal?.nativeElement) { this.bsModal = new bootstrap.Modal(this.addWorkModal.nativeElement); }
    if (this.customItemModal?.nativeElement) { this.bsCustomItemModal = new bootstrap.Modal(this.customItemModal.nativeElement); }
  }

  openNewWorkModal(): void {
    this.clearMessages();
    this.newWorkshopWorkForm.reset();
    this.clientItems = [];
    this.clientItemInputControl.reset();
    this.clearClientSelectionForNewWork();
    if (this.bsModal) { this.bsModal.show(); }
  }

  addClientItem(): void {
    const val = this.clientItemInputControl.value?.trim();
    if (val) {
      this.clientItems.push(val);
      this.clientItemInputControl.reset();
    }
  }

  removeClientItem(index: number): void {
    if (index >= 0 && index < this.clientItems.length) {
      this.clientItems.splice(index, 1);
    }
  }

  selectClientForNewWork(client: Client): void {
    this.selectedClientForNewWork = client;
    this.clientSearchControl.setValue(client.name, { emitEvent: false });
    if (client.id) {
      this.bikesForSelectedClient$ = this.firestoreDataService.getBikesForClient(client.id);
    }
  }

  clearClientSelectionForNewWork(): void {
    this.selectedClientForNewWork = null;
    this.clientSearchControl.setValue('');
    this.bikesForSelectedClient$ = of([]);
    this.clientItems = [];
    this.clientItemInputControl.reset();
    this.newWorkshopWorkForm.reset();
  }

  async onCreateNewWork(): Promise<void> {
    this.clearMessages();
    if (this.newWorkshopWorkForm.invalid || !this.selectedClientForNewWork?.id) {
      this.createWorkError = "Por favor, seleccione un cliente, una bicicleta y complete todos los campos.";
      return;
    }
    this.isCreatingWork = true;
    const formValues = this.newWorkshopWorkForm.value;
    const selectedBike = formValues.selectedBike as Bike;

    // Si el usuario escribió un artículo y no presionó el botón agregar antes de crear, lo incluimos automáticamente
    const pendingItem = this.clientItemInputControl.value?.trim();
    if (pendingItem) {
      this.clientItems.push(pendingItem);
      this.clientItemInputControl.reset();
    }

    try {
      const nextNumber = await this.firestoreDataService.getNextWorkOrderNumber();
      const autoGeneratedCaseNumber = `TRB-${nextNumber}`;
      const newWorkData: Omit<WorkshopWork, 'id' | 'entryDate' | 'billingStatus' | 'currentStatus' | 'notifiedStatus' | 'totalPrice' | 'addedItems'> = {
        caseNumber: autoGeneratedCaseNumber,
        clientId: this.selectedClientForNewWork.id!,
        clientName: this.selectedClientForNewWork.name,
        bikeId: selectedBike.id!,
        bikeDescription: `${selectedBike.brand} ${selectedBike.model || ''} - ${selectedBike.color}`,
        serviceType: formValues.serviceType,
        description: formValues.description,
        clientItems: [...this.clientItems]
      };
      await this.firestoreDataService.addWorkshopWork(newWorkData);
      alert('¡Trabajo creado correctamente!');
      this.clientItems = [];
      this.clientItemInputControl.reset();
      if (this.bsModal) { this.bsModal.hide(); }
    } catch (error: any) {
      this.createWorkError = `Error al crear nuevo trabajo: ${error.message}`;
    } finally {
      this.isCreatingWork = false;
    }
  }
  
  selectWork(work: WorkshopWork): void {
    this.clearMessages();
    if (work?.id) {
      this.selectedWork = work; 
      this.selectedWorkId$.next(work.id); 
    } else {
      this.selectedWork = null;
      this.selectedWorkId$.next(null);
    }
  }

  async onSubmitStatusChange(): Promise<void> {
    this.clearMessages();
    if (!this.selectedWork?.id || this.statusChangeForm.invalid) {
      this.statusUpdateError = "Por favor seleccione un trabajo y un nuevo estado.";
      return;
    }
    this.isUpdatingStatus = true;
    const { newStatus, statusNotes } = this.statusChangeForm.value;
    try {
      await this.firestoreDataService.setWorkStatus(this.selectedWork.id, newStatus, statusNotes);
      alert('¡Estado actualizado correctamente!');
      this.selectedWorkId$.next(this.selectedWork.id);
    } catch (error: any) {
      this.statusUpdateError = `Error al actualizar el estado: ${error.message}`;
    } finally {
      this.isUpdatingStatus = false;
    }
  }

  // --- NUEVO: Validar si un servicio es FULL ---
  isFullService(product: Product | null): boolean {
    if (!product) return false;
    const fullIds = ['234003', '234004', '234005'];
    return fullIds.includes(product.id);
  }

  onAddItemToWork(itemType: 'Labor' | 'Part'): void {
    const form = itemType === 'Labor' ? this.manLaborProductAddForm : this.partsProductAddForm;
    const selectedProduct = form.value.selectedProduct as Product;
    if ((itemType === 'Labor' && selectedProduct?.id === '234001') || (itemType === 'Part' && selectedProduct?.id === '236000')) {
      this.customItemType = itemType;
      this.customItemForm.reset({ quantity: 1, assignee1: null });
      this.bsCustomItemModal.show();
    } else {
      this.addSelectedItemToWork(itemType);
    }
  }

  async addSelectedItemToWork(itemType: 'Labor' | 'Part'): Promise<void> {
    this.clearMessages();
    const form = itemType === 'Labor' ? this.manLaborProductAddForm : this.partsProductAddForm;
    const loadingFlagProp = itemType === 'Labor' ? 'isAddingLaborItem' : 'isAddingPartItem';
    if (!this.selectedWork?.id || form.invalid) {
      this.addItemError = "Por favor seleccione un producto y una cantidad válida.";
      return;
    }
    const formValues = form.value;
    const selectedProduct = formValues.selectedProduct as Product;

    // --- LÓGICA DE ASIGNACIÓN CORREGIDA ---
    const allEmps = await firstValueFrom(this.allActiveEmployees$.pipe(take(1)));
    let mechanicId = null;
    let assistantId = null;
    let mechanicName = null;
    let assistantName = null;

    if (itemType === 'Labor' && this.isFullService(selectedProduct)) {
      const emp1 = allEmps.find(e => e.id === formValues.assignee1);
      const emp2 = allEmps.find(e => e.id === formValues.assignee2);

      // Control para evitar 2 mecánicos o 2 asistentes
      if (emp1 && emp2 && emp1.type === emp2.type) {
        this.addItemError = "Error: Para un servicio FULL, no puede asignar dos empleados con el mismo rol (Debe elegir 1 Mecánico y 1 Asistente).";
        return;
      }

      if (emp1) {
        if (emp1.type === 'Mecánico') { mechanicId = emp1.id; mechanicName = emp1.name; }
        else { assistantId = emp1.id; assistantName = emp1.name; }
      }
      if (emp2) {
        if (emp2.type === 'Mecánico') { mechanicId = emp2.id; mechanicName = emp2.name; }
        else { assistantId = emp2.id; assistantName = emp2.name; }
      }
    } else {
      // Caso Normal (Un solo Dropdown)
      const emp1 = allEmps.find(e => e.id === formValues.assignee1);
      if (emp1) {
        if (emp1.type === 'Mecánico') { mechanicId = emp1.id; mechanicName = emp1.name; }
        else { assistantId = emp1.id; assistantName = emp1.name; }
      }
    }

    this[loadingFlagProp] = true;

    const newItem: Omit<WorkItem, 'id'> = {
      productId: selectedProduct.id,
      productName: selectedProduct.nombre,
      quantity: formValues.quantity,
      unitPrice: selectedProduct.precio,
      itemType: itemType,
      productFamily: selectedProduct.Familia || '', 
      addedDate: new Date(),
      mechanicId: mechanicId,
      assistantId: assistantId,
      mechanicName: mechanicName,
      assistantName: assistantName
    };
    
    try {
      await this.firestoreDataService.addItemToWorkshopWork(this.selectedWork.id, newItem);
      form.reset({ selectedProduct: null, quantity: 1, assignee1: null, assignee2: null });
      if (itemType === 'Labor') this.laborSearchControl.setValue('');
      if (itemType === 'Part') this.partSearchControl.setValue('');
      this.selectedWorkId$.next(this.selectedWork.id);
    } catch (error: any) {
      this.addItemError = `Error al agregar artículo: ${error.message}`;
    } finally {
      this[loadingFlagProp] = false;
    }
  }

  async onAddCustomItem(): Promise<void> {
    this.clearMessages();
    if (!this.selectedWork?.id || this.customItemForm.invalid) {
      this.addItemError = "Por favor, complete el nombre y el precio del artículo personalizado.";
      return;
    }
    const loadingFlagProp = this.customItemType === 'Labor' ? 'isAddingLaborItem' : 'isAddingPartItem';
    this[loadingFlagProp] = true;
    const formValues = this.customItemForm.value;

    const allEmps = await firstValueFrom(this.allActiveEmployees$.pipe(take(1)));
    let mechanicId = null;
    let assistantId = null;
    let mechanicName = null;
    let assistantName = null;

    const emp1 = allEmps.find(e => e.id === formValues.assignee1);
    if (emp1) {
        if (emp1.type === 'Mecánico') { mechanicId = emp1.id; mechanicName = emp1.name; }
        else { assistantId = emp1.id; assistantName = emp1.name; }
    }

    const newItem: Omit<WorkItem, 'id'> = {
      productId: this.customItemType === 'Labor' ? '234001' : '236000',
      productName: formValues.productName,
      quantity: formValues.quantity,
      unitPrice: formValues.unitPrice,
      itemType: this.customItemType,
      addedDate: new Date(),
      mechanicId: mechanicId,
      assistantId: assistantId,
      mechanicName: mechanicName,
      assistantName: assistantName
    };
    
    try {
      await this.firestoreDataService.addItemToWorkshopWork(this.selectedWork.id, newItem);
      this.bsCustomItemModal.hide();
      this.selectedWorkId$.next(this.selectedWork.id);
    } catch (error: any) {
      this.addItemError = `Error al agregar artículo personalizado: ${error.message}`;
    } finally {
      this[loadingFlagProp] = false;
    }
  }

  async onBillingStatusChange(event: Event, work: WorkshopWork): Promise<void> {
    if (!work?.id) return;
    this.isUpdatingBillingStatus = true;
    const input = event.target as HTMLInputElement;
    const newStatus: BillingStatus = input.checked ? 'billed' : 'pending';
    try {
      await this.firestoreDataService.updateBillingStatus(work.id, newStatus);
      this.selectedWorkId$.next(work.id);
    } catch (error) {
      alert("Hubo un error al actualizar el estado de facturación.");
      input.checked = !input.checked;
    } finally {
      this.isUpdatingBillingStatus = false;
    }
  }

  async onNotifiedStatusChange(event: Event, work: WorkshopWork): Promise<void> {
    if (!work?.id) return;
    const input = event.target as HTMLInputElement;
    if (input.checked) {
      const wantsToSendEmail = window.confirm(`¿Deseas notificar al cliente por correo?`);
      if (wantsToSendEmail) {
        this.isUpdatingNotifiedStatus = true;
        try {
          // El método original proporcionado usaba un sendOrderReadyEmail. Mantenemos lo original
          // await this.firestoreDataService.sendOrderReadyEmail(...); 
          await this.firestoreDataService.updateNotifiedStatus(work.id, 'notified');
          alert("Notificación enviada exitosamente.");
          this.selectedWorkId$.next(work.id);
        } catch (emailError: any) {
          alert(`Error al notificar: ${emailError.message}`);
          input.checked = false;
        } finally {
          this.isUpdatingNotifiedStatus = false;
        }
      } else {
        input.checked = false;
      }
    } else {
      this.isUpdatingNotifiedStatus = true;
      try {
        await this.firestoreDataService.updateNotifiedStatus(work.id, 'pending');
        this.selectedWorkId$.next(work.id);
      } catch (error) {
        alert("Hubo un error al actualizar el estado de notificación.");
        input.checked = true;
      } finally {
        this.isUpdatingNotifiedStatus = false;
      }
    }
  }

  async confirmRemoveItem(itemToRemove: WorkItem): Promise<void> {
    this.clearMessages();
    if (!this.selectedWork?.id) { return; }
    if (this.selectedWork.currentStatus === 'Ready' || this.selectedWork.currentStatus === 'Completed') {
      alert("Este trabajo no se puede modificar.");
      return;
    }
    const confirmation = window.confirm(`¿Está seguro de que desea eliminar el artículo "${itemToRemove.productName}"?`);
    if (confirmation) {
      this.isRemovingItem = true;
      try {
        await this.firestoreDataService.removeItemFromWorkshopWork(this.selectedWork.id, itemToRemove);
        this.selectedWorkId$.next(this.selectedWork.id);
      } catch (error: any) {
        this.removeItemError = `Error al eliminar artículo: ${error.message}`;
      } finally {
        this.isRemovingItem = false;
      }
    }
  }

  async printReceipt(work: WorkshopWork | null | undefined): Promise<void> {
    if (!work) return;
    const client = await this.firestoreDataService.getClientById(work.clientId);
    const bike = await this.firestoreDataService.getBikeById(work.bikeId);
    if (!client || !bike) {
      alert("No se pudo obtener la información completa para imprimir.");
      return;
    }
    const printWindow = window.open('', '_blank', 'height=600,width=400');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Ticket de Recepción</title>');
      printWindow.document.write(`<style>
          @media print { body { width: 80mm; margin: 0; padding: 0; } }
          body { width: 80mm; margin: 0 auto; padding: 5mm; font-family: 'Courier New', Courier, monospace; font-size: 10pt; color: #000; box-sizing: border-box; }
          .logo { display: block; margin: 0 auto 10px auto; max-width: 60mm; max-height: 50px; }
          h1 { text-align: center; font-size: 14pt; margin: 0 0 10px 0; padding-bottom: 5px; border-bottom: 1px dashed #000; }
          .info-section { display: flex; flex-direction: column; }
          .info-section p { margin: 3px 0; display: flex; justify-content: space-between; }
          .info-section strong { font-weight: bold; margin-right: 10px; white-space: nowrap; flex-shrink: 0; }
          .info-section span { text-align: right; word-break: break-all; flex-grow: 1; }
          .description-section { margin-top: 15px; border-top: 1px dashed #000; padding-top: 10px; }
          .description-section p { margin-top: 5px; white-space: pre-wrap; }
          .footer { margin-top: 20px; text-align: center; font-size: 9pt; border-top: 1px dashed #000; padding-top: 10px; }
      </style>`);
      printWindow.document.write('</head><body>');
      printWindow.document.write('<img src="assets/logo.png" alt="Logo" class="logo">');
      printWindow.document.write('<h1>Ticket de Recepción</h1>');
      printWindow.document.write('<div class="info-section">');
      printWindow.document.write(`<p><strong>N° Caso:</strong> <span>${work.caseNumber}</span></p>`);
      printWindow.document.write(`<p><strong>Cliente:</strong> <span>${client.name}</span></p>`);
      if(client.phone) printWindow.document.write(`<p><strong>Teléfono:</strong> <span>${client.phone}</span></p>`);
      printWindow.document.write(`<p><strong>Recepción:</strong> <span>${this.datePipe.transform(work.entryDate?.toDate ? work.entryDate.toDate() : work.entryDate, 'dd/MM/yy HH:mm', 'es')}</span></p>`);
      printWindow.document.write(`<p><strong>Bicicleta:</strong> <span>${bike.brand} ${bike.model || ''} - ${bike.color}</span></p>`);
      if (bike.chassis) { printWindow.document.write(`<p><strong>Chasis:</strong> <span>${bike.chassis}</span></p>`); }
      printWindow.document.write('</div>');
      printWindow.document.write(`<div class="description-section"><strong>Descripción del Trabajo:</strong>`);
      printWindow.document.write(`<p>${work.description ? work.description : 'N/A'}</p></div>`);
      if (work.clientItems && work.clientItems.length > 0) {
        printWindow.document.write(`<div class="description-section"><strong>Artículos a Devolver al Cliente:</strong>`);
        printWindow.document.write(`<ul style="margin: 5px 0; padding-left: 20px;">${work.clientItems.map(item => `<li>${item}</li>`).join('')}</ul></div>`);
      }
      printWindow.document.write('<div class="footer"><p>Gracias por su visita.</p></div>');
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 250);
    } else {
      alert("No se pudo abrir la ventana de impresión. Por favor, deshabilite los bloqueadores de pop-ups.");
    }
  }

  getSpanishStatus(statusValue: WorkCurrentStatus | undefined): string {
    if (!statusValue) return 'Desconocido';
    const statusMap: Record<WorkCurrentStatus, string> = {
      'Received': 'Recibido', 'In Progress': 'En Progreso', 'Awaiting Parts': 'Esperando Repuestos',
      'Ready': 'Lista', 'Completed': 'Completado', 'Cancelled': 'Cancelado/Desestimado', 'Handed Off': 'Entregada'
    };
    return statusMap[statusValue] || statusValue;
  }

  getStatusClass(status: WorkCurrentStatus | undefined): string {
    if (!status) return 'bg-secondary';
    switch (status) {
      case 'Received': return 'bg-primary';
      case 'In Progress': return 'bg-info text-dark';
      case 'Awaiting Parts': return 'bg-warning text-dark';
      case 'Ready': return 'bg-success';
      case 'Completed': return 'bg-dark';
      case 'Cancelled': return 'bg-danger';
      default: return 'bg-light text-dark';
    }
  }


  private clearMessages() {
    this.statusUpdateError = null;
    this.addItemError = null;
    this.createWorkError = null;
    this.removeItemError = null;
  }
}